
import { AlertCircle, ChevronDown, Search, X } from "lucide-react";
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";

import { cn } from "./cn";
import styles from "./Field.module.css";
import { IconButton } from "./Button";
import type { Tone } from "./status";

export type FieldIds = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
};

export type FieldProps = {
  label: string;
  labelHidden?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  labelAction?: ReactNode;
  inline?: boolean;
  className?: string;
  htmlFor?: string;
  children: ReactNode | ((ids: FieldIds) => ReactNode);
};

export function Field({
  label,
  labelHidden,
  hint,
  error,
  required,
  labelAction,
  inline,
  className,
  htmlFor,
  children,
}: FieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const ids: FieldIds = { id, describedBy, invalid: Boolean(error) };

  return (
    <div className={cn(styles.field, inline && styles.fieldInline, className)}>
      <div className={styles.fieldCopy}>
        <div className={styles.fieldLabelRow}>
          <label className={cn(styles.fieldLabel, labelHidden && styles.srOnlyLabel)} htmlFor={id}>
            {label}
            {required ? (
              <span className={styles.required} aria-hidden="true">
                {" *"}
              </span>
            ) : null}
          </label>
          {labelAction}
        </div>
        {hint ? (
          <div className={styles.fieldHint} id={hintId}>
            {hint}
          </div>
        ) : null}
      </div>
      {typeof children === "function" ? children(ids) : children}
      {error ? (
        <InlineMessage tone="danger" id={errorId}>
          {error}
        </InlineMessage>
      ) : null}
    </div>
  );
}

export type InlineMessageProps = {
  tone?: Tone;
  icon?: ReactNode;
  id?: string;
  className?: string;
  children: ReactNode;
};

const INLINE_TONE_CLASS: Record<Tone, string> = {
  neutral: styles.inlineNeutral,
  success: styles.inlineSuccess,
  warning: styles.inlineWarning,
  danger: styles.inlineDanger,
  information: styles.inlineInformation,
};

export function InlineMessage({ tone = "neutral", icon, id, className, children }: InlineMessageProps) {
  return (
    <p
      className={cn(styles.inlineMessage, INLINE_TONE_CLASS[tone], className)}
      id={id}
      role={tone === "danger" ? "alert" : undefined}
    >
      {icon ?? (tone === "danger" ? <AlertCircle className={styles.inlineIcon} size={12} strokeWidth={1.75} aria-hidden="true" /> : null)}
      <span>{children}</span>
    </p>
  );
}

type ControlSize = "md" | "lg";

const CONTROL_SIZE_CLASS: Record<ControlSize, string> = {
  md: styles.controlMd,
  lg: styles.controlLg,
};

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label: string;
  labelHidden?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  labelAction?: ReactNode;
  controlSize?: ControlSize;
  mono?: boolean;
  iconBefore?: ReactNode;
  suffix?: ReactNode;
  fieldClassName?: string;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    labelHidden,
    hint,
    error,
    required,
    labelAction,
    controlSize = "md",
    mono,
    iconBefore,
    suffix,
    fieldClassName,
    className,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      required={required}
      labelAction={labelAction}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <span className={cn(styles.inputWrap, iconBefore && styles.withIcon, suffix && styles.withSuffix)}>
          {iconBefore ? <span className={styles.inputIcon}>{iconBefore}</span> : null}
          <input
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-required={required || undefined}
            className={cn(styles.control, CONTROL_SIZE_CLASS[controlSize], mono && styles.mono, className)}
            {...rest}
          />
          {suffix ? <span className={styles.inputSuffix}>{suffix}</span> : null}
        </span>
      )}
    </Field>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  labelHidden?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  labelAction?: ReactNode;
  mono?: boolean;
  fieldClassName?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, labelHidden, hint, error, required, labelAction, mono, fieldClassName, className, rows = 3, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      required={required}
      labelAction={labelAction}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          className={cn(styles.control, styles.textarea, mono && styles.mono, className)}
          {...rest}
        />
      )}
    </Field>
  );
});

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size" | "children"> & {
  label: string;
  labelHidden?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
  controlSize?: ControlSize;
  fieldClassName?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    labelHidden,
    hint,
    error,
    required,
    options,
    placeholder,
    controlSize = "md",
    fieldClassName,
    className,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <span className={styles.selectWrap}>
          <select
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-required={required || undefined}
            className={cn(styles.control, CONTROL_SIZE_CLASS[controlSize], styles.select, className)}
            {...rest}
          >
            {placeholder ? <option value="">{placeholder}</option> : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className={styles.selectIcon} size={16} strokeWidth={1.75} aria-hidden="true" />
        </span>
      )}
    </Field>
  );
});

export type SearchFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  placeholder?: string;
  className?: string;
  id?: string;
};

export function SearchField({ value, onValueChange, label, placeholder, className, id }: SearchFieldProps) {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className={cn(styles.inputWrap, styles.withIcon, value && styles.withSuffix, className)} role="search">
      <label className={styles.srOnlyLabel} htmlFor={inputId}>
        {label}
      </label>
      <Search className={styles.inputIcon} size={16} strokeWidth={1.75} aria-hidden="true" />
      <input
        id={inputId}
        type="search"
        className={cn(styles.control, styles.controlMd)}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onValueChange(event.target.value)}
      />
      {value ? (
        <span className={styles.searchClear}>
          <IconButton
            label={t("ui.clearSearch")}
            size="sm"
            showTooltip={false}
            icon={<X size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => onValueChange("")}
          />
        </span>
      ) : null}
    </div>
  );
}
